import React, { useContext, useEffect, useState, useRef } from 'react'
import SearchLocationContext from '../../context/SearchLocationContext'
import LocationItem from './LocationItem/LocationItem'
import styles from './LocationList.module.scss'
import ErrorContainer from '../ErrorContainer/ErrorContainer'
import SearchBox from './SearchBox/SearchBox'
import Filters from './Filters/Filters'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'
import FiltersList from '../FiltersList/FiltersList'
import { useFilters, useWindowSize } from '../../hooks'
import ApplicationContext from '../../context/ApplicationContext'
import Map from '../GoogleMap/Map/Map'
import EventsList from '../EventsList/EventsList'
import Spacer from '../Spacer/Spacer'
import MapActive from '../../assets/media/icons/map-active.svg'
import MapInactive from '../../assets/media/icons/map-inactive.svg'
import ListActive from '../../assets/media/icons/list-active.svg'
import ListInactive from '../../assets/media/icons/list-inactive.svg'
import LoadingDots from '../LoadingDots/LoadingDots'

const LocationList = () => {
    const [searchLocationData, setSearchLocationData] = useContext(SearchLocationContext)
    const [applicationData, setApplicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const { openFiltersList, toggleFiltersList, appliedFilters, updateFilters } = useFilters()
    const { isMobile } = useWindowSize()
    const searchBoxRef = useRef(null)
    const filtersRef = useRef(null)
    const foundContainerRef = useRef(null)
    const contentRef = useRef(null)
    const eventsListRef = useRef(null)
    const [announcement, setAnnouncement] = useState('')

    const listViewHandler = () => {
        setApplicationData(prev => {
            return {
                ...prev,
                mapViewMobile: false,
            }
        })
    }

    const mapViewHandler = () => {
        setApplicationData(prev => {
            return {
                ...prev,
                mapViewMobile: true,
            }
        })
    }

    /* this effect handles the transition from desktop view to mobile in case the user
    squeezes the browser window in width */
    useEffect(() => {
        if (isMobile === undefined) {
            return
        }
        if (!isMobile) {
            setApplicationData(prev => {
                return {
                    ...prev,
                    mapViewMobile: false,
                }
            })
        }
    }, [isMobile])

    // Hide main content from screen readers and keyboard when modal is open
    useEffect(() => {
        const refs = [searchBoxRef, filtersRef, foundContainerRef, contentRef, eventsListRef]

        refs.forEach(ref => {
            if (ref.current) {
                if (openFiltersList) {
                    ref.current.setAttribute('aria-hidden', 'true')
                    ref.current.setAttribute('inert', '')
                } else {
                    ref.current.removeAttribute('aria-hidden')
                    ref.current.removeAttribute('inert')
                }
            }
        })
    }, [openFiltersList])

    // Update announcement when location list changes
    useEffect(() => {
        if (!searchLocationData?.locationsLoading) {
            const baseAnnouncement = searchLocationData?.locationList?.length > 0
                ? `${searchLocationData.locationList.length} ${localizations.fittersFound}`
                : localizations.noFittingLocationsFoundTitle

            // Append zero-width space to make each announcement unique, forcing screen readers to announce
            // This is more reliable than setTimeout and works across all screen readers
            setAnnouncement(`${baseAnnouncement}\u200B`.repeat(Math.random() > 0.5 ? 1 : 2))
        }
    }, [searchLocationData?.locationList?.length, searchLocationData?.locationsLoading, localizations])

    const onLocationClick = location => {
        setSearchLocationData(prevState => {
            return {
                ...prevState,
                prevMapState: {
                    ...prevState,
                },
                activeLocationId: location.locationId,
                highlightedLocationId: location.locationId,
                defaultZoom: applicationData?.locationClickZoom,
                disableRoamingRequest: true,
                mapCenter: {
                    lat: location.latitude,
                    lng: location.longitude,
                },
            }
        })

        setApplicationData(prevState => {
            return {
                ...prevState,
                overlay: true,
            }
        })

        if (window && document) {
            const mapContent = document.querySelector('fittinglocationmap')

            if (mapContent) {
                const offsetAmount = mapContent.offsetTop ? -mapContent.offsetTop : 0
                mapContent.scrollIntoView()
                window.scrollBy(0, offsetAmount)
            }
        }
    }

    const handleSearch = filters => {
        updateFilters(filters, true)
        toggleFiltersList()
    }

    return (
        <div className={styles.locationListContainer}>
            <div ref={searchBoxRef}>
                <SearchBox />
            </div>
            <div ref={filtersRef}>
                <Filters toggleFiltersList={toggleFiltersList} appliedFilters={appliedFilters} />
            </div>
            <Spacer border />
            <div ref={foundContainerRef} className={styles.foundContainer}>
                {/* Visual count display */}
                <div className={`${styles.foundText} ${searchLocationData?.locationsLoading ? styles.loading : ''}`}>
                    {searchLocationData?.locationList?.length} {localizations.fittersFound}
                </div>
                {/* Screen reader announcement (hidden) */}
                <div
                    className={styles.srOnly}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {announcement}
                </div>

                {isMobile && (
                    <ToggleSwitch
                        leftImageActive={MapActive}
                        leftImageInactive={MapInactive}
                        leftHandler={mapViewHandler}
                        rightHandler={listViewHandler}
                        rightImageActive={ListActive}
                        rightImageInactive={ListInactive}
                        controller={applicationData.mapViewMobile}
                    />
                )}
            </div>
            {applicationData.mapViewMobile && isMobile ? (
                <div ref={contentRef} className={styles.mapView___mobile}>
                    <Map />
                </div>
            ) : (
                <div ref={contentRef} className={styles.locationsListWrapper}>
                    <div className={styles.locationsList}>
                        {searchLocationData?.locationsLoading ? (
                            <LoadingDots text={`${localizations.findingFitters}...`} />
                        ) : searchLocationData?.locationList?.length > 0 ? (
                            <>
                                {searchLocationData.locationList.map(item => {
                                    return (
                                        <div
                                            key={item?.locationId}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => onLocationClick(item)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    onLocationClick(item)
                                                }
                                            }}
                                            className={styles.locationButton}
                                            aria-label={`View details for ${item?.name}`}
                                        >
                                            <LocationItem item={item} />
                                        </div>
                                    )
                                })}
                            </>
                        ) : searchLocationData.error ? (
                            <ErrorContainer errorMessage={localizations.errors.filterLocationsCanNotBeDislayed} />
                        ) : (
                            <div className={styles.noLocationsContainer}>
                                <div className={styles.noLocationsTitle}>{localizations.noFittingLocationsFoundTitle}</div>
                                <div className={styles.noLocationsText}>{localizations.noFittingLocationsFoundDescription}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div ref={eventsListRef}>
                <EventsList />
            </div>
            <FiltersList
                onSearch={handleSearch}
                updateFilters={updateFilters}
                appliedFilters={appliedFilters}
                filters={searchLocationData?.availableFilterCategories}
                openFiltersList={openFiltersList}
                toggleFiltersList={toggleFiltersList}
            />
        </div>
    )
}

export default LocationList
